"use client";

import {useReducedMotion} from "motion/react";
import {useEffect, useRef} from "react";

const RIPPLE_DURATION_MS = 650;
/** Fades content in behind the wave so it settles exactly as the ripple finishes. */
export const DASH_RIPPLE_REVEAL_TRANSITION = {
   delay: (RIPPLE_DURATION_MS * 0.25) / 1000,
   duration: (RIPPLE_DURATION_MS * 0.75) / 1000,
   ease: "easeInOut",
} as const;
/** How much dimmer the wave is in the corners than at the center. */
const EDGE_DIMMING = 0.5;
/** Px the wavefront bulges in and out around the ring, so it isn't a perfect ellipse. */
const WAVEFRONT_WOBBLE = 18;
/** Px of per-dash scatter on top of the wobble, so the front looks grainy rather than drawn. */
const DASH_SCATTER = 10;
/** The dimmest a dash can be relative to its neighbours, so the wave shimmers. */
const MIN_DASH_BRIGHTNESS = 0.45;
/**
 * Share of the canvas that must be on screen before the ripple starts, so it plays
 * once the sheet has nearly finished sliding in rather than during the slide.
 */
const DASH_RIPPLE_VISIBLE_AMOUNT = 0.9;

type DashRippleCanvasProps = {
   /** A ripple began; content should fade in behind it. */
   onRippleStart?: () => void;
   /** The canvas left the screen entirely and will ripple again when it returns. */
   onRippleReset?: () => void;
};

/** A brief dash ripple each time the welcome message comes into view. */
export default function DashRippleCanvas({onRippleStart, onRippleReset}: DashRippleCanvasProps) {
   const canvasRef = useRef<HTMLCanvasElement>(null);
   const reducedMotion = useReducedMotion();
   // Read through a ref so new callback identities don't restart the animation.
   const callbacksRef = useRef({onRippleStart, onRippleReset});
   callbacksRef.current = {onRippleStart, onRippleReset};

   useEffect(() => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext("2d");
      if (!canvas || !context) return;

      let width = 0;
      let height = 0;
      let frame = 0;
      let visible = false;
      let elapsed = 0;
      let started = false;
      let startedAt: number | null = null;
      let rippleRadius = 0;
      let color = "";
      let dashes: {x: number; y: number; distance: number; fade: number}[] = [];
      // Randomness only varies decorative geometry and brightness; it is not security-sensitive.
      // Picked once per mount so a resize keeps the same wavefront shape.
      const wobblePhases = [Math.random(), Math.random()].map((value) => value * Math.PI * 2);

      const draw = () => {
         context.clearRect(0, 0, width, height);
         if (reducedMotion || elapsed >= RIPPLE_DURATION_MS) return;
         color ||= getComputedStyle(canvas).color;
         context.fillStyle = color;
         const progress = elapsed / RIPPLE_DURATION_MS;
         const entrance = Math.min(elapsed / 80, 1);
         for (const dash of dashes) {
            const wave = Math.max(0, 1 - Math.abs(dash.distance - progress * rippleRadius) / 70) ** 2;
            context.globalAlpha = wave * 0.2 * dash.fade * entrance * (1 - progress ** 2);
            context.fillRect(dash.x - 1.5, dash.y - 4.5, 3, 9);
         }
         context.globalAlpha = 1;
      };

      const animate = (time: number) => {
         startedAt ??= time - elapsed;
         elapsed = Math.min(time - startedAt, RIPPLE_DURATION_MS);
         draw();
         if (elapsed < RIPPLE_DURATION_MS) frame = requestAnimationFrame(animate);
      };

      const syncAnimation = () => {
         cancelAnimationFrame(frame);
         startedAt = null;
         if (!visible || document.hidden) return;
         draw();
         if (reducedMotion || elapsed >= RIPPLE_DURATION_MS) return;
         if (!started) {
            started = true;
            callbacksRef.current.onRippleStart?.();
         }
         frame = requestAnimationFrame(animate);
      };

      const resize = () => {
         const bounds = canvas.getBoundingClientRect();
         width = bounds.width;
         height = bounds.height;
         rippleRadius = Math.hypot(width / 2, height * 0.65 / 2) + 70 + WAVEFRONT_WOBBLE + DASH_SCATTER / 2;
         const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
         canvas.width = Math.round(width * pixelRatio);
         canvas.height = Math.round(height * pixelRatio);
         context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
         dashes = [];
         if (!width || !height) return;

         for (let x = (width % 10) / 2; x < width; x += 10) {
            for (let y = (height % 16) / 2; y < height; y += 16) {
               const dx = x - width / 2;
               const dy = y - height / 2;
               // 0 at the center, 1 at the corners, so every dash in the panel gets a share of the wave.
               const radius = Math.hypot(dx / width, dy / height) * Math.SQRT2;
               const fade = 1 - EDGE_DIMMING * radius * radius;
               const angle = Math.atan2(dy, dx);
               const wobble = WAVEFRONT_WOBBLE * (0.6 * Math.sin(3 * angle + wobblePhases[0]) + 0.4 * Math.sin(5 * angle + wobblePhases[1]));
               const scatter = (Math.random() - 0.5) * DASH_SCATTER;
               const brightness = MIN_DASH_BRIGHTNESS + (1 - MIN_DASH_BRIGHTNESS) * Math.random();
               dashes.push({x, y, distance: Math.hypot(dx, dy * 0.65) + wobble + scatter, fade: fade * brightness});
            }
         }
         draw();
      };

      const resizeObserver = new ResizeObserver(resize);
      // Partly hidden pauses the ripple; fully hidden (the sheet closed) rewinds it
      // so the next open plays it again. Rewinding only once nothing is on screen
      // keeps a closing sheet from losing its content mid-slide.
      const intersectionObserver = new IntersectionObserver(([entry]) => {
         visible = entry.isIntersecting && entry.intersectionRatio >= DASH_RIPPLE_VISIBLE_AMOUNT;
         if (!entry.isIntersecting && started) {
            started = false;
            elapsed = 0;
            callbacksRef.current.onRippleReset?.();
         }
         syncAnimation();
      }, {threshold: [0, DASH_RIPPLE_VISIBLE_AMOUNT]});
      resizeObserver.observe(canvas);
      intersectionObserver.observe(canvas);
      document.addEventListener("visibilitychange", syncAnimation);

      return () => {
         cancelAnimationFrame(frame);
         resizeObserver.disconnect();
         intersectionObserver.disconnect();
         document.removeEventListener("visibilitychange", syncAnimation);
      };
   }, [reducedMotion]);

   return <canvas ref={canvasRef} aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full text-600 motion-reduce:hidden" />;
}
